import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, Search, Check } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/src/components/ui/popover';
import { Switch } from '@repo/ui/src/components/ui/switch';
import { Label } from '@repo/ui/src/components/ui/label';
import { cn } from '@repo/ui/src/lib/utils';
import { ENROLLMENT_MODES, type EnrollmentMode, type ClassGroupListRow } from '@repo/shared-types';
import { classColorStyle } from '../../../lib/classColors';
import { normalizeForSearch } from '../../../lib/textSearch';

export interface StudentFilters {
  showInactive: boolean;
  noEnrollment: boolean;
  noSlot: boolean;
  subjectIds: string[];
  /** Hình thức học — how the student pays. Empty means "any". */
  modes: EnrollmentMode[];
  tags: string[];
  /** THI-267 Task 15: Lớp filter, `?classIds=` — same `enrollments.some`
   *  fold as `subjectIds`/`modes`/`noSlot` (see `useStudents.ts`), so it is
   *  unreachable together with `noEnrollment` exactly like those two. */
  classIds: string[];
}

/** Active CRITERIA, not values: three selected subjects count once. Counting
 *  values pushes the badge into the high single digits and it stops meaning
 *  anything. */
function activeFilterCount(f: StudentFilters): number {
  return (
    (f.showInactive ? 1 : 0) +
    (f.noEnrollment ? 1 : 0) +
    (f.noSlot ? 1 : 0) +
    (f.subjectIds.length ? 1 : 0) +
    (f.modes.length ? 1 : 0) +
    (f.tags.length ? 1 : 0) +
    (f.classIds.length ? 1 : 0)
  );
}

/** Chip label per mode. A lookup, not `t(\`list.mode${m}\`)`: a literal key is
 *  greppable, and a missing one fails at review rather than at runtime. */
const MODE_LABEL_KEY: Record<EnrollmentMode, string> = {
  SESSION_MONTHLY: 'list.modeSessionMonthly',
  SESSION_TERM: 'list.modeSessionTerm',
  MONTHLY_FLAT: 'list.modeMonthlyFlat',
};

export function StudentFilterPanel({
  filters, onChange, subjects, tags, classes,
}: {
  filters: StudentFilters;
  onChange: (next: StudentFilters) => void;
  subjects: { id: string; name: string }[];
  tags: string[];
  /** THI-267 Task 15: the org's own classes (ACTIVE + ENDED, ARCHIVED
   *  already excluded) — fetched once by `StudentsPage` and passed down so
   *  this stays a pure render, same convention as `subjects`/`tags` above. */
  classes: ClassGroupListRow[];
}) {
  const { t } = useTranslation('students');
  const count = activeFilterCount(filters);

  const toggleIn = <T extends string>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((x) => x !== value) : [...list, value];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={count > 0 ? 'default' : 'outline'}
          size="icon"
          // The badge is a visual-only sibling `<span>` — it doesn't get read by
          // itself, so the count has to fold into the label or it's never announced.
          aria-label={count > 0 ? `${t('list.filters')} (${count})` : t('list.filters')}
          // THI-267 Task 15 (S8/2): below `sm` the button becomes "Lọc · n" text
          // instead of an icon + corner badge — that badge is legible beside a
          // desktop search bar but too small a target to read at 390px. `cn`
          // resolves the width/padding conflict against the `icon` size's own
          // `h-9 w-9` (tailwind-merge keeps whichever comes later in the string).
          className="relative shrink-0 max-sm:w-auto max-sm:gap-1.5 max-sm:px-3"
        >
          <Filter className="h-4 w-4" />
          <span className="hidden max-sm:inline text-sm font-medium">
            {count > 0 ? t('list.filterButtonCount', { count }) : t('list.filterButtonShort')}
          </span>
          {count > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-700 px-1 text-[10px] font-semibold text-white max-sm:hidden">
              {count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 max-h-[70vh] overflow-y-auto p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-surface-500">
            {t('list.filters')}
          </span>
          {count > 0 && (
            <button
              type="button"
              onClick={() => onChange({
                showInactive: false, noEnrollment: false, noSlot: false, subjectIds: [], modes: [], tags: [], classIds: [],
              })}
              className="text-xs font-medium text-brand-700 hover:underline"
            >
              {t('list.filterClear')}
            </button>
          )}
        </div>

        <div className="space-y-3 px-4 py-3">
          {([
            ['showInactive', 'list.filterShowInactive'],
            ['noEnrollment', 'list.filterNoEnrollment'],
            ['noSlot', 'list.filterNoSlot'],
          ] as const).map(([key, label]) => {
            // noEnrollment writes `enrollments.none`; noSlot and subject both write
            // `enrollments.some` — the same student row can never satisfy both, so
            // noEnrollment + either one returns 0 rows BY CONSTRUCTION. Disable
            // noSlot (and the subject chips below) rather than let that
            // unreachable combination be picked at all.
            const disabled = key === 'noSlot' && filters.noEnrollment;
            return (
              <div key={key}>
                <div className="flex items-center justify-between gap-3">
                  <Label
                    htmlFor={`f-${key}`}
                    className={cn('text-sm font-normal', disabled && 'opacity-50')}
                  >
                    {t(label)}
                  </Label>
                  <Switch
                    id={`f-${key}`}
                    checked={filters[key]}
                    disabled={disabled}
                    // A disabled control with no stated reason is a dead end for
                    // a screen-reader user; point at the line that explains it.
                    aria-describedby={disabled ? 'filter-noenrollment-note' : undefined}
                    onCheckedChange={(v) => {
                      if (key === 'noEnrollment' && v) {
                        // Clear noSlot/subjectIds/modes/classIds on the way in so
                        // the badge count and the query never disagree with what's
                        // still visibly selectable once this switch disables them.
                        onChange({ ...filters, noEnrollment: true, noSlot: false, subjectIds: [], modes: [], classIds: [] });
                      } else {
                        onChange({ ...filters, [key]: v });
                      }
                    }}
                  />
                </div>
                {key === 'noEnrollment' && filters.noEnrollment && (
                  <p id="filter-noenrollment-note" className="mt-1 text-xs text-muted-foreground">
                    {t('list.filterNoEnrollmentExclusive')}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {subjects.length > 0 && (
          <ChipGroup
            title={t('list.filterSubjects')}
            options={subjects.map((s) => ({ value: s.id, label: s.name }))}
            selected={filters.subjectIds}
            onToggle={(v) => onChange({ ...filters, subjectIds: toggleIn(filters.subjectIds, v) })}
            disabled={filters.noEnrollment}
            describedBy={filters.noEnrollment ? 'filter-noenrollment-note' : undefined}
          />
        )}
        <ChipGroup
          title={t('list.filterModes')}
          options={ENROLLMENT_MODES.map((m) => ({ value: m, label: t(MODE_LABEL_KEY[m]) }))}
          selected={filters.modes}
          // ChipGroup is string-typed for the subject/tag groups; this is the
          // one call site whose values are narrower, so the cast lives here
          // rather than loosening toggleIn for everyone.
          onToggle={(v) => onChange({ ...filters, modes: toggleIn(filters.modes, v as EnrollmentMode) })}
          disabled={filters.noEnrollment}
          describedBy={filters.noEnrollment ? 'filter-noenrollment-note' : undefined}
        />
        <ClassFilterSection
          classes={classes}
          selected={filters.classIds}
          onToggle={(id) => onChange({ ...filters, classIds: toggleIn(filters.classIds, id) })}
          disabled={filters.noEnrollment}
          describedBy={filters.noEnrollment ? 'filter-noenrollment-note' : undefined}
          t={t}
        />
        {tags.length > 0 && (
          <ChipGroup
            title={t('list.filterTags')}
            options={tags.map((x) => ({ value: x, label: x }))}
            selected={filters.tags}
            onToggle={(v) => onChange({ ...filters, tags: toggleIn(filters.tags, v) })}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

function ChipGroup({ title, options, selected, onToggle, disabled, describedBy }: {
  title: string;
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
  disabled?: boolean;
  /** Id of the line explaining WHY the group is disabled. The explanation sits a
   *  section away, so without this a screen-reader user meets an inert group
   *  with no stated reason. */
  describedBy?: string;
}) {
  return (
    <div
      className={cn('border-t px-4 py-3', disabled && 'opacity-50 cursor-not-allowed')}
      aria-describedby={disabled ? describedBy : undefined}
    >
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-surface-500">{title}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const on = selected.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              disabled={disabled}
              onClick={() => onToggle(o.value)}
              aria-pressed={on}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                disabled && 'pointer-events-none',
                on
                  ? 'border-brand-600 bg-brand-600 text-white'
                  : 'border-surface-200 text-surface-600 hover:bg-surface-100',
              )}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * THI-267 Task 15: the "Lớp" filter entry (spec §5 "Students list", handoff
 * decision 13) — a search box plus one row per class (colour square,
 * `displayName`, subject muted, `rosterCount`), multi-select, ended classes
 * (`phase === 'ENDED'`) sorted last. `classes` already excludes ARCHIVED
 * (`StudentsPage` only ever fetches ACTIVE + ENDED) and already carries
 * ended rows after active ones (two `useClasses` calls concatenated in that
 * order), so no client-side sort is needed here — only the search filter.
 *
 * A dedicated section rather than another `ChipGroup`: a chip grid doesn't
 * have room for the subject/count columns the spec asks for, and a dozen+
 * classes need to be searchable rather than all rendered as chips at once.
 */
function ClassFilterSection({
  classes, selected, onToggle, disabled, describedBy, t,
}: {
  classes: ClassGroupListRow[];
  selected: string[];
  onToggle: (id: string) => void;
  disabled?: boolean;
  describedBy?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const [search, setSearch] = useState('');

  if (classes.length === 0) return null;

  const query = normalizeForSearch(search);
  const filtered = query ? classes.filter((c) => normalizeForSearch(c.displayName).includes(query)) : classes;

  return (
    <div
      className={cn('border-t px-4 py-3', disabled && 'opacity-50 cursor-not-allowed')}
      aria-describedby={disabled ? describedBy : undefined}
    >
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-surface-500">{t('list.filterClasses')}</p>
      <div className="mb-2 flex h-8 items-center gap-1.5 rounded-md border border-surface-200 bg-surface-50 px-2">
        <Search className="h-3.5 w-3.5 shrink-0 text-surface-400" />
        <input
          type="text"
          value={search}
          disabled={disabled}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('list.filterClassesSearch')}
          className="w-full min-w-0 bg-transparent text-[13px] text-surface-800 placeholder:text-surface-400 focus:outline-none disabled:cursor-not-allowed"
        />
      </div>
      <div className="flex max-h-52 flex-col gap-0.5 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="px-1.5 py-2 text-xs text-surface-400">{t('list.filterClassesEmpty')}</p>
        ) : (
          filtered.map((c) => {
            const on = selected.includes(c.id);
            const ended = c.phase === 'ENDED';
            const style = classColorStyle(c.color);
            return (
              <button
                key={c.id}
                type="button"
                disabled={disabled}
                onClick={() => onToggle(c.id)}
                aria-pressed={on}
                className={cn(
                  'flex items-center gap-2 rounded-md px-1.5 py-1.5 text-left text-[13px] transition-colors',
                  disabled && 'pointer-events-none',
                  on ? 'bg-brand-50' : 'hover:bg-surface-100',
                )}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    on ? 'border-brand-600 bg-brand-600 text-white' : 'border-surface-300',
                  )}
                  aria-hidden
                >
                  {on && <Check className="h-3 w-3" strokeWidth={3} />}
                </span>
                {style ? (
                  <span className="h-2.5 w-2.5 shrink-0 rounded-[3px] border opacity-90" style={style} aria-hidden />
                ) : (
                  <span className="h-2.5 w-2.5 shrink-0 rounded-[3px] border border-dashed border-surface-300" aria-hidden />
                )}
                <span className="min-w-0 flex-1 truncate">
                  <span className={cn('font-medium', ended ? 'text-surface-400' : 'text-surface-800')}>
                    {c.displayName}
                  </span>
                  <span className="ml-1 text-surface-400">
                    · {ended ? t('list.filterClassesEnded') : c.subject.name}
                  </span>
                </span>
                <span className="shrink-0 text-[11.5px] tabular-nums text-surface-400">{c.rosterCount}</span>
              </button>
            );
          })
        )}
      </div>
      <p className="mt-2 text-[11px] text-surface-400">{t('list.filterClassesFootnote')}</p>
    </div>
  );
}
